import { type BundleFiltersData, BundleStep } from './BundleModuleTypes';
import type BundlePackage from '@itinerary/bundle/BundlePackage';
import BundlePackageSet from '@itinerary/bundle/BundlePackageSet';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import type ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import type PriceClass from '@itinerary/PriceClass';
import type FlightModule from '@itinerary/trip/flight/FlightModule';
import type HotelModule from '@itinerary/hotel/HotelModule';
import ItineraryModule from '@itinerary/ItineraryModule';
import {
    type EmptyListReason,
    type Filter,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import { ReserveType } from '@utils/itinerary';
import { type BundleUrlParams } from '@utils/bundle';
import { type Location } from '@utils/types';
import { type BundlePackageLoaderConfig } from '@itinerary/bundle/types';

export default class BundleModule extends ItineraryModule<
    PriceClass,
    BundleFiltersData,
    Location
> {
    //#region state
    departureReserveType: ReserveType = ReserveType.Flight;
    returnReserveType: ReserveType = ReserveType.Flight;
    currentStep: BundleStep = BundleStep.Residence;

    override itineraryPackageSets: [BundlePackageSet | null] = [null];
    override get reserveInfo() {
        return useBundleForm().reserveInfo;
    }

    override sortBy = 'price';

    vehicleReserveType: ReserveType = ReserveType.Flight;

    selectedBundlePackage: BundlePackage | null = null;
    //#endregion

    get bundleVehicleTypeTitle(): string {
        if (this.vehicleReserveType === ReserveType.Flight) {
            return 'پرواز';
        }
        throw new Error('type case not handled!');
    }

    getFlightModule(): FlightModule {
        return useItineraryStore().bundleFlightModule as FlightModule;
    }

    getHotelModule(): HotelModule {
        return useItineraryStore().bundleHotelModule as HotelModule;
    }

    getModuleBaseOnReserveType(reserveType: ReserveType): ItineraryModule {
        switch (reserveType) {
            case ReserveType.Hotel:
                return this.getHotelModule();
            case ReserveType.Flight:
                return this.getFlightModule();
            default:
                throw new Error('Reserve type not handled !');
        }
    }

    override get metaTagTitle(): string {
        const bundleForm = useBundleForm();
        return `بسته سفر از ${bundleForm.origin?.title} به ${bundleForm.destination?.title} | مِستربلیط`;
    }

    override get emptyListReason(): EmptyListReason {
        return {
            image: `/img/no-result/${this.reserveInfo.type}.svg`,
            title: 'در تاریخ و مقصد مورد نظر',
            notFoundText: `${this.reserveInfo.nameE} موجود نیست!`,
        };
    }

    override loadSuggestions(): void {
        throw new Error('Operation is not supported!');
    }

    get currentSelectingReserveType(): ReserveType {
        switch (this.currentStep) {
            case BundleStep.Residence:
                return ReserveType.Hotel;
            case BundleStep.Departure:
                return this.departureReserveType;
            case BundleStep.Return:
                return this.returnReserveType;
            default:
                throw new Error('Step type not handled!');
        }
    }

    getCurrentModule<T extends ItineraryModule = ItineraryModule>(): T {
        switch (this.currentStep) {
            case BundleStep.Residence:
                return this.getHotelModule() as any;
            case BundleStep.Departure:
                return this.getModuleBaseOnReserveType(
                    this.departureReserveType
                ) as any;
            case BundleStep.Return:
                return this.getModuleBaseOnReserveType(
                    this.returnReserveType
                ) as any;
            default:
                throw new Error('Step type not handled!');
        }
    }

    override get currentPackageSet():
        | ItineraryPackageSet<
              BundleFiltersData['meta'],
              BundleFiltersData['key']
          >
        | null
        | undefined {
        switch (this.currentStep) {
            case BundleStep.Residence:
                return this.itineraryPackageSets[0];

            case BundleStep.Departure:
                return this.getFlightModule()
                    .itineraryPackageSets[0] as ItineraryPackageSet;

            case BundleStep.Return:
                return this.getFlightModule()
                    .itineraryPackageSets[1] as ItineraryPackageSet;

            default:
                throw new Error('Step type not handled!');
        }
    }

    override get list(): ItineraryPackage<PriceClass>[] {
        if (!this.currentItineraryPackages || !this.currentPackageSet) {
            return [];
        }
        return this.currentItineraryPackages.filter((pkg: ItineraryPackage) =>
            this.applyFilter(pkg, this.currentPackageSet!.filters)
        );
    }

    override get sortByList(): SortBy[] {
        return this.getCurrentModule().sortByList;
    }

    override get defaultSortBy(): string {
        return this.getCurrentModule().defaultSortBy;
    }

    override applySort(
        list: ItineraryPackage<PriceClass>[]
    ): ItineraryPackage<PriceClass>[] {
        if (this.currentStep === BundleStep.Residence) {
            const hotelSorted = this.getCurrentModule<HotelModule>().applySort(
                (list as BundlePackage[]).map(p => p.hotelPackage)
            );
            return hotelSorted.map(
                h => (list as BundlePackage[]).find(p => p.hotelPackage === h)!
            );
        } else {
            return this.getCurrentModule().applySort(list);
        }
    }

    override get sortedReservableList(): ItineraryPackage<PriceClass>[] {
        return this.applySort(this.list);
    }

    override get sortedNonReservableList(): ItineraryPackage<PriceClass>[] {
        return [];
    }

    override get emptyPackage(): ItineraryPackage {
        throw new Error('Operation not supported!');
    }

    override handleFirstDateChange(date: Date | null): void {
        const bundleForm = useBundleForm();
        const newToDate = dayConfig
            .gregory(date)
            .add(
                dayConfig
                    .gregory(bundleForm.toDate)
                    .diff(bundleForm.fromDate, 'day'),
                'day'
            )
            .toDate();
        bundleForm.fromDate = date;
        bundleForm.toDate = newToDate;
    }

    override get description(): string | null {
        const bundleForm = useBundleForm();
        return `سفرتو بچین بریم ${bundleForm.destination?.title}`;
    }

    override get descriptionTemplate(): string {
        return 'سفرتو بچین بریم';
    }

    override get canonicalRegex(): RegExp {
        return /(\/tour)\/(\d+)\/(\d+)$/;
    }

    override getInitialFilters(): any {
        return this.getCurrentModule().getInitialFilters();
    }

    override getPackageSet(
        loaderConfig: BundlePackageLoaderConfig
    ): BundlePackageSet {
        return new BundlePackageSet(loaderConfig, this.getInitialFilters());
    }

    override get dateTitle(): string {
        const { fromDate, toDate } = useBundleForm();
        const startDay = dayConfig.gregory(fromDate).startOf('day');
        const duration = dayConfig.gregory(toDate).diff(startDay, 'day');
        return `${formatDateRange(fromDate!, toDate || new Date())}${
            duration ? ` (${duration} شب)` : ''
        }`; // never happens 0 nights, but to making sure 😅
    }

    override getItineraryPackageLoaderConfigs(
        params: BundleUrlParams
    ): BundlePackageLoaderConfig[] {
        const bundleForm = useBundleForm();
        return [
            {
                originCode: params.originCode,
                destinationCode: params.destinationCode,
                fromDate: dayConfig
                    .gregory(params.fromDate)
                    .format(formatter()),
                toDate: dayConfig.gregory(params.toDate).format(formatter()),
                rooms: bundleForm.mapQueryRoomsToPaxes(params.rooms).map(r => ({
                    adultCount: r[0].count,
                    childCount: r[1].count,
                    infantCount: r[2].count,
                })),
                url: useRoute().path,
            },
        ];
    }

    override loadLaterals(): Promise<void> {
        return Promise.resolve();
    }

    override get haveTracking(): boolean {
        return false;
    }

    loadPackageSetResults() {
        // not used and not work, just to keep it alongside others
        this.getModuleBaseOnReserveType(
            ReserveType.Hotel
        ).itineraryPackageSets[0] =
            this.itineraryPackageSets[0]?.hotelPackageSet || null;
    }

    async selectBundlePackage(pkg: BundlePackage) {
        const hotelModule = this.getHotelModule();
        hotelModule.currentSelectedPackageId = null;
        const packageSelectStore = usePackageSelectStore();
        packageSelectStore.removeSelectedPackage(0);
        this.selectedBundlePackage = pkg;
        const hotelPackage = await hotelModule.loadHotelPackage({
            adultCount: pkg.totalAdultCount,
            childCount: pkg.totalChildCount,
            infantCount: pkg.totalInfantCount || 0,
            fromDate: pkg.hotelPackage.fromDate!,
            toDate: pkg.hotelPackage.toDate!,
            cityName: pkg.hotelPackage.cityName,
            hotelName: pkg.hotelPackage.engTitle,
            roomCount: pkg.roomsCount,
        });
        if (hotelPackage) {
            hotelPackage.roomsPackageList = pkg.hotelRoomsPackages;
            packageSelectStore.selectedPackages = [
                hotelPackage,
                pkg.defaultDeparturePackage,
                pkg.defaultReturnPackage,
            ];
            const [firstPackage] = pkg.hotelRoomsPackages;
            if (firstPackage) {
                hotelModule.currentSelectedPackageId = firstPackage.id;
                hotelModule.selectRoomsPackage(firstPackage);
            }
            hotelModule.isLoadingRooms = false;
            packageSelectStore.loadPreInvoiceForCurrentPackages();
        } else {
            throw new Error('هتل یافت نشد !');
        }
    }

    override formAutoReserve(): Promise<void> {
        throw new Error('The method is not implemented.');
    }

    override get filters(): Filter[] {
        return this.getCurrentModule().filters;
    }

    override applyFilter(
        pkg: ItineraryPackage<PriceClass>,
        filters: Record<string, any>
    ): boolean {
        if (this.currentStep === BundleStep.Residence) {
            return this.getHotelModule().applyFilter(
                (pkg as BundlePackage).hotelPackage,
                filters as any
            );
        } else {
            return this.getCurrentModule().applyFilter(pkg, filters);
        }
    }

    override get currentItineraryPackages():
        | ItineraryPackage<PriceClass>[]
        | null {
        if (this.currentStep === BundleStep.Residence) {
            return this.itineraryPackageSets[0]?.list || [];
        } else {
            return this.currentPackageSet?.list ?? [];
        }
    }

    override get mainTitle(): string {
        const bundleForm = useBundleForm();
        return `بسته سفر از ${bundleForm.origin?.title} به ${bundleForm.destination?.title}`;
    }

    override get alternativeSuggestionTypes(): [ReserveType, ReserveType] {
        throw new Error('Operation is not supported!');
    }
}
